import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useGetCatalogStats, getGetCatalogStatsQueryKey } from "@workspace/api-client-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { useAuth } from "@/hooks/use-auth";
import { Search as SearchIcon, ArrowRight } from "lucide-react";
import { track } from "@/lib/track";
import { useT, useFormatNumber } from "@/i18n";

export default function Home() {
  const [, navigate] = useLocation();
  const [q, setQ] = useState("");
  const [searchError, setSearchError] = useState(false);
  const [exampleIndex, setExampleIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const [focused, setFocused] = useState(false);
  const { user, requireSignIn } = useAuth();
  const t = useT();
  const fmtNum = useFormatNumber();
  const goToScan = () =>
    requireSignIn({
      reason: t("signInModal.reasonScan"),
      onSuccess: () => navigate("/scan"),
    });
  const stats = useGetCatalogStats({ query: { queryKey: getGetCatalogStatsQueryKey(), staleTime: 60_000 } });

  const examples = [
    t("home.heroSearchExample1"),
    t("home.heroSearchExample2"),
    t("home.heroSearchExample3"),
    t("home.heroSearchExample4"),
    t("home.heroSearchExample5"),
    t("home.heroSearchExample6"),
    t("home.heroSearchExample7"),
  ];
  const showHint = q.trim() === "";
  const rotating = showHint && !focused;

  useEffect(() => {
    if (!rotating) return;
    let fadeTimer: ReturnType<typeof setTimeout>;
    const cycle = setInterval(() => {
      setFading(true);
      fadeTimer = setTimeout(() => {
        setExampleIndex((i) => (i + 1) % examples.length);
        setFading(false);
      }, 350);
    }, 3200);
    return () => {
      clearInterval(cycle);
      clearTimeout(fadeTimer);
    };
  }, [rotating, examples.length]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const tt = q.trim();
    if (!tt) {
      setSearchError(true);
      return;
    }
    setSearchError(false);
    track("search", { source: "home", has_query: true });
    navigate(`/search?q=${encodeURIComponent(tt)}`);
  }

  function handleQChange(value: string) {
    setQ(value);
    if (searchError && value.trim()) setSearchError(false);
  }

  return (
    <div className="flex flex-col min-h-[100dvh]">
      <Header onScanClick={goToScan} />
      <main className="flex-1 flex flex-col">
        <section className="relative flex-1 flex flex-col bg-card overflow-x-hidden">
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{ backgroundImage: "repeating-linear-gradient(135deg, hsl(var(--foreground)) 0 1px, transparent 1px 12px)" }}
          />
          <div
            className="relative w-full mx-auto max-w-7xl px-4 md:px-8 py-10 md:py-0 flex flex-col md:grid md:grid-cols-12 gap-8 md:gap-10 md:items-center md:min-h-[calc(100vh-4rem)]"
          >
            {/* Left — headline + subtitle + search */}
            <div className="md:col-span-7 flex flex-col">
              <h1
                className="font-serif font-black leading-[1.05] md:leading-[0.95] tracking-tight break-words"
                style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)" }}
              >
                {t("home.heroTitleLine1")}
                <br />
                <span className="text-[hsl(var(--primary))]">{t("home.heroTitleLine2")}</span>
              </h1>
              <p
                className="mt-4 text-muted-foreground max-w-xl"
                style={{ fontSize: "clamp(0.95rem, 1.25vw, 1.15rem)" }}
              >
                {t("home.heroSubtitle")}
              </p>
              <form
                id="hero-search"
                onSubmit={submit}
                className={`mt-7 flex flex-col sm:flex-row border-2 bg-background max-w-xl shadow-[6px_6px_0_0_hsl(var(--foreground))] ${
                  searchError ? "border-destructive" : "border-foreground"
                }`}
              >
                <div className="flex flex-1 items-center">
                  <SearchIcon className="ml-4 h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="relative flex-1 min-w-0">
                    <input
                      value={q}
                      onChange={(e) => handleQChange(e.target.value)}
                      onFocus={() => setFocused(true)}
                      onBlur={() => setFocused(false)}
                      aria-label={t("home.heroSearchAriaLabel")}
                      aria-invalid={searchError}
                      type="search"
                      inputMode="search"
                      enterKeyHint="search"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      className="w-full bg-transparent px-3 py-4 text-base focus:outline-none"
                    />
                    {showHint && (
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none absolute inset-0 flex items-center px-3 text-base text-muted-foreground truncate transition-opacity duration-300 ${fading ? "opacity-0" : "opacity-100"}`}
                      >
                        {examples[exampleIndex]}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="submit"
                  className="bg-foreground text-background px-7 py-4 sm:py-0 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--primary))] transition-colors border-t-2 sm:border-t-0 sm:border-l-2 border-foreground min-h-[48px]"
                >
                  {t("home.heroSearchButton")}
                </button>
              </form>
              {searchError && (
                <p role="alert" className="mt-2 text-sm font-medium text-destructive max-w-xl">
                  {t("header.searchRequired")}
                </p>
              )}
              {user?.isStaff && (
                <div className="mt-4 flex flex-wrap gap-3 items-center text-sm">
                  <Link
                    href="/browse"
                    className="inline-flex items-center gap-2 min-h-[44px] px-3 hover:text-[hsl(var(--primary))]"
                  >
                    {t("home.browseArchive")} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </div>

            {/* Right — stats card */}
            <div className="md:col-span-5">
              <div
                className="border-2 border-foreground bg-background shadow-[8px_8px_0_0_hsl(var(--primary))]"
                style={{ padding: "clamp(1.5rem, 3vw, 3rem)" }}
              >
                {stats.data ? (
                  <div className="text-center md:text-left">
                    <div
                      className="font-serif font-black leading-none text-[hsl(var(--primary))]"
                      style={{ fontSize: "clamp(3.5rem, 8vw, 7rem)" }}
                    >
                      {fmtNum(stats.data.shirts)}
                    </div>
                    <div className="mt-4 font-mono font-black text-base md:text-xl uppercase tracking-[0.15em] text-foreground">
                      {t("home.stat.shirtsCatalogued")}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="h-24 animate-shimmer bg-muted" />
                    <div className="mt-4 h-4 w-1/2 animate-shimmer bg-muted" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
